// The child page's "Messages" tab: every practice idea sent for this child, by
// the signed-in adult AND by the others helping the child, so whoever reviews
// the child sees what was suggested before (owner request 2026-10-23).
//
// One card per SEND. A send is the rows sharing a batch_id (create_suggestions,
// 1–3 games with one note); a row with no batch_id was sent on its own before
// grouping existed, so it is its own card. The sender is named only in the
// family's words for that adult (link label, role) and their organisation —
// never an email — and "You" for the caller's own sends.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fill, type Locale, type PortalDictionary } from "../../i18n/dictionary";
import { getPortalData } from "../../data";
import type { ChildSuggestion } from "../../data/types";
import { assignmentStatus, formatInstant } from "../../progress/text";
import { roleLabel } from "../admin/Kids";
import { SectionTitle } from "./OnePagerView";
import { gameName } from "./gameName";

export interface SuggestionGroup {
  key: string;
  sentAt: string;
  note: string | null;
  fromMe: boolean;
  senderRole: ChildSuggestion["senderRole"];
  senderLabel: string | null;
  senderOrg: string | null;
  items: ChildSuggestion[];
}

/** Rows → one group per send (batch id; a null batch id is its own group), newest first. */
export function groupSuggestions(rows: readonly ChildSuggestion[]): SuggestionGroup[] {
  const byKey = new Map<string, SuggestionGroup>();
  for (const r of rows) {
    const key = r.batchId ? `batch:${r.batchId}` : `single:${r.id}`;
    const g = byKey.get(key);
    if (!g) {
      byKey.set(key, {
        key,
        sentAt: r.sentAt,
        note: r.note,
        fromMe: r.fromMe,
        senderRole: r.senderRole,
        senderLabel: r.senderLabel,
        senderOrg: r.senderOrg,
        items: [r],
      });
    } else {
      g.items.push(r);
      if (r.sentAt > g.sentAt) g.sentAt = r.sentAt;
      if (!g.note && r.note) g.note = r.note;
    }
  }
  // Within a send, the order the games were chosen in: create_suggestions
  // stamps them 1 ms apart, so oldest first IS that order.
  for (const g of byKey.values()) g.items.sort((a, b) => (a.sentAt < b.sentAt ? -1 : a.sentAt > b.sentAt ? 1 : 0));
  return [...byKey.values()].sort((a, b) => (a.sentAt < b.sentAt ? 1 : a.sentAt > b.sentAt ? -1 : 0));
}

type T = PortalDictionary["service"]["childDetail"];

/** "You", else "Ms Aisyah · Therapist · Bright Steps", else "Another helper". */
export function senderLine(t: T, g: Pick<SuggestionGroup, "fromMe" | "senderLabel" | "senderRole" | "senderOrg">): string {
  if (g.fromMe) return t.senderYou;
  const parts = [g.senderLabel, g.senderRole ? roleLabel(t, g.senderRole) : null, g.senderOrg].filter(
    (x): x is string => !!x && x.trim() !== "",
  );
  return parts.length > 0 ? parts.join(" · ") : t.senderOther;
}

const box = {
  background: "var(--surface)",
  border: "1px solid var(--line)",
  borderRadius: "var(--r-lg)",
} as const;

export function ChildMessages({
  childId,
  canSee,
  isOwner = false,
  locale,
  dict,
}: {
  childId: string;
  canSee: boolean;
  /** The child's owner sees every idea; a helper only their own (enforced server-side, migration 0011). */
  isOwner?: boolean;
  locale: Locale;
  dict: PortalDictionary;
}) {
  const t = dict.service.childDetail;
  const p = dict.service.progress;
  const navigate = useNavigate();
  const [rows, setRows] = useState<ChildSuggestion[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);
  const retry = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    setRows(null);
    setFailed(false);
    getPortalData()
      .childSuggestions(childId)
      .then((r) => {
        if (alive) setRows(r);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [childId, canSee, tick]);

  let body: React.ReactNode;
  if (!canSee) {
    body = <p style={{ ...box, color: "var(--ink-soft)", fontSize: 15, padding: "16px 20px", margin: 0 }}>{t.messagesHidden}</p>;
  } else if (failed) {
    body = (
      <div role="alert" style={{ ...box, background: "var(--peach-tint)", borderColor: "var(--peach)", padding: "16px 20px", color: "var(--peach-deep)", fontSize: 15 }}>
        <p style={{ margin: "0 0 10px" }}>{t.messagesError}</p>
        <button
          type="button"
          onClick={retry}
          style={{ minHeight: 44, padding: "0 18px", border: "1.5px solid var(--peach-deep)", background: "transparent", color: "var(--peach-deep)", borderRadius: "var(--r-pill)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 14.5, cursor: "pointer" }}
        >
          {t.messagesRetry}
        </button>
      </div>
    );
  } else if (rows === null) {
    body = (
      <p role="status" style={{ ...box, color: "var(--ink-soft)", fontSize: 15, padding: "16px 20px", margin: 0 }}>
        {t.messagesLoading}
      </p>
    );
  } else if (rows.length === 0) {
    body = <p style={{ ...box, color: "var(--ink-soft)", fontSize: 15, padding: "16px 20px", margin: 0 }}>{t.messagesNone}</p>;
  } else {
    const groups = groupSuggestions(rows);
    body = (
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
        {groups.map((g) => (
          <li key={g.key} style={{ ...box, padding: "14px 18px", minWidth: 0 }}>
            <article aria-label={`${senderLine(t, g)}, ${fill(t.sentOn, { date: formatInstant(locale, g.sentAt) })}`}>
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: "4px 12px" }}>
                <b style={{ fontSize: 15, color: "var(--ink-deep)", overflowWrap: "anywhere" }}>{senderLine(t, g)}</b>
                <span style={{ fontSize: 13, color: "var(--ink-faint)" }}>
                  {fill(t.sentOn, { date: formatInstant(locale, g.sentAt) })}
                </span>
              </div>
              {g.note && (
                <p style={{ margin: "8px 0 0", fontSize: 14.5, color: "var(--ink)", lineHeight: 1.5, overflowWrap: "anywhere" }}>{g.note}</p>
              )}
              <ul aria-label={t.sentGames} style={{ listStyle: "none", margin: "10px 0 0", padding: 0, borderTop: "1px solid var(--line)" }}>
                {g.items.map((a) => (
                  <li
                    key={a.id}
                    style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "2px 12px", paddingBlock: 8, borderBottom: "1px solid var(--line)" }}
                  >
                    <span style={{ fontSize: 15, color: "var(--ink-deep)", fontWeight: 600, minWidth: 0 }}>{gameName(a.target, locale)}</span>
                    <span style={{ fontSize: 13, color: "var(--ink-soft)", fontWeight: 600 }}>{assignmentStatus(p, locale, a)}</span>
                  </li>
                ))}
              </ul>
            </article>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section aria-labelledby="messages-title" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <SectionTitle id="messages-title">{t.messagesTitle}</SectionTitle>
        {canSee && (
          <button
            type="button"
            onClick={() => navigate(`/assign?child=${encodeURIComponent(childId)}`)}
            style={{ minHeight: 44, padding: "0 18px", border: "1.5px solid var(--brand)", background: "transparent", color: "var(--brand)", borderRadius: "var(--r-pill)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 14.5, cursor: "pointer" }}
          >
            {t.messagesSend}
          </button>
        )}
      </div>
      {canSee && <p style={{ margin: 0, fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.5 }}>{isOwner ? t.messagesIntro : t.messagesIntroHelper}</p>}
      {body}
    </section>
  );
}
